import * as Toggle from '@radix-ui/react-toggle-group'
import {
  ActionsContainer,
  DraggableWrapper,
  DropIndicator,
  LeftSideInfo,
  PrimaryLineContainer,
  useDragHandlers,
} from 'features/kestrelViewer/kestrelViewerMarkup/utils/DragAndDropComponents'
import { useEntryBehavior } from 'features/kestrelViewer/kestrelViewerMarkup/utils/useEntryBehavior'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import React, { FC, useState } from 'react'
import styled from 'styled-components'
import { KVMeasurement } from 'types'

import { DeleteMeasurementButton } from './DeleteMeasurementButton'
import { EditMeasurementButton } from './EditMeasurementButton'
import { ToggleMeasurementVisibilityButton } from './ToggleMeasurementVisibilityButton'

const StyledMeasurementEntry = styled(Toggle.Item)<{ $selected: boolean }>`
  all: unset;
  cursor: ${({ $selected }) => ($selected ? 'auto' : 'pointer')} !important;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.forecast.background4 : ''} !important;
  position: relative;
  padding: 0.25rem;

  &:hover {
    opacity: ${({ $selected }) => ($selected ? 0.8 : 1)} !important;
    background-color: ${({ theme, $selected }) =>
      $selected
        ? theme.forecast.background4
        : theme.forecast.background3} !important;
  }
`

const MeasurementLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`

export const MeasurementEntry: FC<{
  measurement: KVMeasurement
  selectedMarkupId: string | undefined
  onReorder?: (
    draggedId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => void
}> = ({ measurement, onReorder }) => {
  const [isHovered, setIsHovered] = useState(false)

  const { isSelected, handleClick, handleHover, handleHoverEnd } =
    useEntryBehavior(
      measurement,
      KestrelCommands.HIGHLIGHT_MEASUREMENT,
      KestrelCommands.UNHIGHLIGHT_ALL_MEASUREMENTS,
      'measurementId'
    )

  const {
    dropPosition,
    isDragging,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragHandlers(measurement, onReorder || (() => {}), 'measurementid')

  return (
    <StyledMeasurementEntry
      value={measurement.id}
      $selected={isSelected}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      onMouseEnter={() => {
        setIsHovered(true)
        handleHover()
      }}
      onMouseLeave={() => {
        setIsHovered(false)
        handleHoverEnd()
      }}>
      {dropPosition && <DropIndicator $position={dropPosition} />}
      <DraggableWrapper
        $isDragging={isDragging}
        draggable={true}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}>
        <PrimaryLineContainer $visible={measurement.visible}>
          <LeftSideInfo>
            <MeasurementLabel>{measurement.label}</MeasurementLabel>
          </LeftSideInfo>
          {isHovered && (
            <ActionsContainer>
              <EditMeasurementButton onClick={handleClick} />
              <ToggleMeasurementVisibilityButton measurement={measurement} />
              <DeleteMeasurementButton measurementId={measurement.id} />
            </ActionsContainer>
          )}
        </PrimaryLineContainer>
      </DraggableWrapper>
    </StyledMeasurementEntry>
  )
}
